import { IUser } from "../../types/reducers/authReducer";
import { isVolunteerOnly } from "../../utils/permissions";
import { ONBOARDING_ROOT } from "../../features/OnboardingPreview/stepRoutes";

// Re-exported from the flow's own route module so the route table, every step URL and
// this redirect cannot disagree. It became "/registration" on 2026-09-24, when the
// redesign stopped being a preview behind an unguessable prefix and became the
// registration flow everyone uses.
export { ONBOARDING_ROOT };



/**
 * Where to send someone after they sign in, or null to leave them alone.
 *
 * Both conditions, and STARTED is the one that matters. A missing or null
 * `onboarding_started_at` means this account never entered the new flow --
 * every user the live /register page creates, every seeded user, every
 * account that predates the feature -- and none of them may be routed.
 * Redirecting is opt-in by evidence, never the default for doing nothing.
 */
export const onboardingRedirectFor = (user: IUser | null | undefined): string | null => {
  if (!user) return null;
  if (!user.onboarding_started_at) return null;
  return user.onboarding_completed_at ? null : ONBOARDING_ROOT;
};

export const VOLUNTEERING_ROOT = "/volunteering";
export const DASHBOARD_ROOT = "/dashboard";

/**
 * Where someone lands after signing in.
 *
 * The unfinished-signup redirect wins over everything: a person mid-signup goes
 * back to signup, whatever else is true of him. Only then does a volunteer-only
 * user -- one holding `manage_volunteering` and none of the app's own
 * permissions -- go to his page instead of a dashboard that would be empty and
 * meaningless to him.
 *
 * DEVIATION FROM THE TASK-12 BRIEF: the brief's own sketch of this function
 * read `user.permissions` as a flat `string[]` (`perms.includes("manage_volunteering")`).
 * The real `IUser.permissions` (src/types/reducers/authReducer.ts) is
 * `{ general: string[]; scoped: { permissions: string[]; ... } }` -- the same
 * shape `utils/permissions.ts`'s `hasPermission` already flattens (general OR
 * scoped, not general alone). Re-implemented on top of that existing helper
 * instead of hand-rolling a second, narrower flattener here.
 */
export const landingRouteFor = (user: IUser | null | undefined): string => {
  const unfinished = onboardingRedirectFor(user);
  if (unfinished) return unfinished;

  // Whole-branch review fix (MINOR 6), 2026-09-08: this was a third
  // hand-written copy of "volunteering is all he can do". It now shares the
  // single definition in utils/permissions.ts with the AppBar gear and
  // hocs/accountRoute.tsx. Fix round 2: that definition is keyed on the
  // workspace ROLE, not on the permission shape this used to test -- see
  // isVolunteerOnly for the `limited`-member regression that forced the change.
  return isVolunteerOnly(user) ? VOLUNTEERING_ROOT : DASHBOARD_ROOT;
};
